import { useEffect, useMemo, useState } from 'react';
import { Fragment } from 'react';
import { useLocation, useResolvedPath } from 'react-router-dom';
import {
  BreadcrumbEntry,
  PageLayout,
  createFrontendModule,
  useApi,
  useBreadcrumbEntries,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { PluginHeader } from '@backstage/ui';

/**
 * Adds the entity to the breadcrumb trail on catalog entity pages.
 *
 * Backstage builds the trail from BreadcrumbEntry registrations, and the only
 * one on an entity page comes from PageLayout itself:
 *
 *   <BreadcrumbEntry entry={{ label: title, ... }}>{content}</BreadcrumbEntry>
 *
 * The catalog plugin passes title="Catalog", and nothing registers the entity —
 * so the trail is just "Catalog" on every entity page.
 *
 * The entry has to nest INSIDE that one to come after it; registering from a
 * wrapper above PageLayout would order it first and read "Haleon > Catalog".
 * Since PageLayout is a swappable component, the supported way in is to swap
 * it, which means restating its (short) implementation and wrapping `children`
 * one level deeper.
 *
 * Registering from the entity's content tabs instead was the cheaper option and
 * is deliberately not taken: Overview and Dependencies are ours but TechDocs
 * and APIs are not, so the trail would change as you moved between tabs.
 */

// /catalog/:namespace/:kind/:name
const ENTITY_PATH = /^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/;

// Entity titles change rarely and the trail re-renders on every tab change;
// without this the lookup would refire constantly.
const titleCache = new Map<string, string>();

function useEntityCrumb(): { label: string; href: string } | undefined {
  const { pathname } = useLocation();
  const catalogApi = useApi(catalogApiRef);

  const match = ENTITY_PATH.exec(pathname);
  const namespace = match?.[1];
  const kind = match?.[2];
  const name = match?.[3] ? decodeURIComponent(match[3]) : undefined;
  const ref = kind && namespace && name ? `${kind}:${namespace}/${name}` : '';

  // Falls back to the entity name so the crumb is right immediately and only
  // sharpens to the display title once the lookup lands.
  const [label, setLabel] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!ref) {
      setLabel(undefined);
      return undefined;
    }
    const cached = titleCache.get(ref);
    if (cached) {
      setLabel(cached);
      return undefined;
    }
    let active = true;
    catalogApi
      .getEntityByRef(ref)
      .then(e => {
        if (!active || !e) return;
        const title = e.metadata.title ?? e.metadata.name;
        titleCache.set(ref, title);
        setLabel(title);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [ref, catalogApi]);

  if (!ref || !name) return undefined;
  return {
    label: label ?? name,
    href: `/catalog/${namespace}/${kind}/${name}`,
  };
}

const pageLayout = SwappableComponentBlueprint.make({
  name: 'core-page-layout',
  params: define =>
    define({
      component: PageLayout,
      loader: () => props => {
        const {
          title,
          icon,
          noHeader,
          titleLink,
          headerActions,
          tabs,
          children,
        } = props;

        const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');
        const resolvedTabs = useMemo(
          () =>
            tabs?.map(tab => ({
              ...tab,
              href: tab.href.startsWith('/')
                ? tab.href
                : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
              matchStrategy: 'prefix' as const,
            })),
          [tabs, parentPath],
        );
        const { items: breadcrumbs } = useBreadcrumbEntries();
        const entityCrumb = useEntityCrumb();

        if (noHeader) {
          return <>{children}</>;
        }

        // The only departure from the stock implementation: children are
        // wrapped so the entity registers one level below the page title.
        const body = entityCrumb ? (
          <BreadcrumbEntry entry={entityCrumb}>{children}</BreadcrumbEntry>
        ) : (
          children
        );

        const content = (
          <Fragment>
            <PluginHeader
              title={title}
              icon={icon}
              titleLink={titleLink}
              breadcrumbs={breadcrumbs}
              tabs={resolvedTabs}
              customActions={headerActions}
            />
            {body}
          </Fragment>
        );

        if (!title) {
          return content;
        }
        return (
          <BreadcrumbEntry
            entry={{ label: title, href: titleLink ?? (parentPath || '/') }}
          >
            {content}
          </BreadcrumbEntry>
        );
      },
    }),
});

export const breadcrumbsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [pageLayout],
});
